(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class WorkshopScene {
    constructor(root) {
      this.root = root;
      this.hero = root.querySelector('.ws3d-hero') || root;
      this.canvas = root.querySelector('[data-ws3d-scene]');
      this.ctx = this.canvas && this.canvas.getContext('2d');
      this.pointer = { x: 0, y: 0 };
      this.target = { x: 0, y: 0 };
      this.time = 0;
      this.frame = null;
      this.isVisible = true;
      this.resize = this.resize.bind(this);
      this.draw = this.draw.bind(this);
      this.onPointer = this.onPointer.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.init();
    }

    init() {
      this.root.classList.add('is-enhanced');
      this.resize();
      window.addEventListener('resize', this.resize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.hero.addEventListener('pointermove', this.onPointer, { passive: true });
      this.setupTilt();
      this.setupReveal();
      if ('IntersectionObserver' in window) {
        this.sceneObserver = new IntersectionObserver(([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.updateAnimation();
        }, { rootMargin: '100px' });
        this.sceneObserver.observe(this.hero);
      }
      if (!reduceMotion && this.ctx) this.updateAnimation();
      else if (this.ctx) this.draw();
    }

    resize() {
      if (!this.canvas) return;
      const rect = this.canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      this.canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      this.canvas.style.width = `${rect.width}px`;
      this.canvas.style.height = `${rect.height}px`;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.width = rect.width;
      this.height = rect.height;
    }

    onPointer(event) {
      const rect = this.hero.getBoundingClientRect();
      const rawX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      const rawY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
      this.target.x = Math.max(-1, Math.min(1, rawX));
      this.target.y = Math.max(-1, Math.min(1, rawY));
      this.root.style.setProperty('--pointer-x', this.target.x.toFixed(3));
      this.root.style.setProperty('--pointer-y', this.target.y.toFixed(3));
    }

    onVisibility() {
      this.updateAnimation();
    }

    updateAnimation() {
      const shouldRun = !reduceMotion && this.ctx && this.isVisible && !document.hidden;
      if (shouldRun && this.frame === null) this.frame = requestAnimationFrame(this.draw);
      if (!shouldRun && this.frame !== null) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    draw() {
      this.frame = null;
      if (!this.ctx || !this.width || !this.height) return;
      this.pointer.x += (this.target.x - this.pointer.x) * 0.045;
      this.pointer.y += (this.target.y - this.pointer.y) * 0.045;
      this.time += 0.006;
      const c = this.ctx;
      const w = this.width;
      const h = this.height;
      c.clearRect(0, 0, w, h);

      const horizon = h * 0.57 + this.pointer.y * 18;
      c.save();
      c.strokeStyle = 'rgba(255, 92, 31, .20)';
      c.lineWidth = 1;
      const vanX = w * 0.68 + this.pointer.x * 60;
      for (let i = -12; i <= 12; i++) {
        c.beginPath();
        c.moveTo(vanX, horizon);
        c.lineTo(vanX + i * w * 0.09, h);
        c.stroke();
      }
      for (let i = 0; i < 18; i++) {
        const z = (i / 18 + this.time) % 1;
        const eased = z * z;
        const y = horizon + eased * (h - horizon);
        c.globalAlpha = eased * 0.8;
        c.beginPath();
        c.moveTo(0, y);
        c.lineTo(w, y);
        c.stroke();
      }
      c.restore();

      c.save();
      for (let i = 0; i < 60; i++) {
        const seed = i * 97.13;
        const z = (Math.sin(seed) * 0.5 + 0.5 + this.time * 0.16) % 1;
        const x = (Math.sin(seed * 1.7) * 0.5 + 0.5) * w;
        const y = (Math.cos(seed * 2.1) * 0.5 + 0.5) * horizon;
        const size = 0.4 + z * 1.8;
        c.fillStyle = `rgba(255,255,255,${0.08 + z * 0.35})`;
        c.fillRect(x + this.pointer.x * z * 20, y + this.pointer.y * z * 10, size, size);
      }
      c.restore();
      if (!reduceMotion && this.isVisible && !document.hidden) this.frame = requestAnimationFrame(this.draw);
    }

    setupTilt() {
      this.root.querySelectorAll('[data-ws3d-tilt]').forEach((card) => {
        if (reduceMotion) return;
        card.addEventListener('pointermove', (event) => {
          const rect = card.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          card.style.setProperty('--tilt-x', `${(-y * 8).toFixed(2)}deg`);
          card.style.setProperty('--tilt-y', `${(x * 10).toFixed(2)}deg`);
          card.style.setProperty('--shine-x', `${((x + .5) * 100).toFixed(0)}%`);
          card.style.setProperty('--shine-y', `${((y + .5) * 100).toFixed(0)}%`);
        });
        card.addEventListener('pointerleave', () => {
          card.style.setProperty('--tilt-x', '0deg');
          card.style.setProperty('--tilt-y', '0deg');
        });
      });
    }

    setupReveal() {
      const items = this.root.querySelectorAll('.ws3d-reveal');
      if (!('IntersectionObserver' in window) || reduceMotion) {
        items.forEach((item) => item.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
      items.forEach((item) => observer.observe(item));
      this.observer = observer;
    }

    destroy() {
      if (this.frame) cancelAnimationFrame(this.frame);
      window.removeEventListener('resize', this.resize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.hero.removeEventListener('pointermove', this.onPointer);
      this.observer && this.observer.disconnect();
      this.sceneObserver && this.sceneObserver.disconnect();
    }
  }

  const scenes = new Map();
  const mount = (scope = document) => scope.querySelectorAll('[data-ws3d]').forEach((root) => {
    if (!scenes.has(root)) scenes.set(root, new WorkshopScene(root));
  });
  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-ws3d]');
    if (root && scenes.has(root)) { scenes.get(root).destroy(); scenes.delete(root); }
  });
})();
